// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputSeparator/AiLabelParser.js

/**
 * Nutrient name mappings for label parsing.
 * Maps label text (case-insensitive) to nutrient keys and default units.
 */
const NUTRIENT_MAPPINGS = {
  'vitamin d': { key: 'vitaminD_µg', unit: 'mcg' },
  'calcium': { key: 'calcium_mg', unit: 'mg' },
  'iron': { key: 'iron_mg', unit: 'mg' },
  'potassium': { key: 'potassium_mg', unit: 'mg' },
  'cholesterol': { key: 'cholesterol_mg', unit: 'mg' },
  'vitamin a': { key: 'vitaminA_µg_RE', unit: 'mcg' },
  'vitamin c': { key: 'vitaminC_mg', unit: 'mg' },
  'vitamin e': { key: 'vitaminE_mg_aTE', unit: 'mg' },
  'thiamin': { key: 'thiamin_mg', unit: 'mg' },
  'riboflavin': { key: 'riboflavin_mg', unit: 'mg' },
  'niacin': { key: 'niacin_NE', unit: 'mg' },
  'vitamin b6': { key: 'vitaminB6_mg', unit: 'mg' },
  'folate': { key: 'folate_µg', unit: 'mcg' },
  'vitamin b12': { key: 'vitaminB12_µg', unit: 'mcg' },
  'biotin': { key: 'biotin_µg', unit: 'mcg' },
  'pantothenic acid': { key: 'pantothenicAcid_mg', unit: 'mg' },
  'phosphorus': { key: 'phosphorus_mg', unit: 'mg' },
  'iodine': { key: 'iodine_µg', unit: 'mcg' },
  'magnesium': { key: 'magnesium_mg', unit: 'mg' },
  'zinc': { key: 'zinc_mg', unit: 'mg' },
  'selenium': { key: 'selenium_µg', unit: 'mcg' },
  'copper': { key: 'copper_µg', unit: 'mcg' },
  'manganese': { key: 'manganese_mg', unit: 'mg' },
  'chromium': { key: 'chromium_µg', unit: 'mcg' },
  'molybdenum': { key: 'molybdenum_µg', unit: 'mcg' },
  'choline': { key: 'choline_mg', unit: 'mg' },
};

/**
 * Parse a LABEL: text line and convert percentage-only nutrients to absolute amounts.
 * Uses the user's personalized daily value targets when available.
 * 
 * Example input:
 *   "LABEL: ANGUS BEEF; serving size 4 oz (112g); calories 270; total fat 19g; 
 *    saturated fat 10g; trans fat 0g; cholesterol 55mg; sodium 40mg; 
 *    total carbohydrate 0g; dietary fiber 0g; total sugars 0g; protein 23g; 
 *    vitamin D 0mcg; calcium 0%; iron 10%."
 * 
 * Output (with user's NNR2023 targets: iron 9mg, calcium 950mg):
 *   "LABEL: ANGUS BEEF; serving size 4 oz (112g); calories 270; total fat 19g; 
 *    saturated fat 10g; trans fat 0g; cholesterol 55mg; sodium 40mg; 
 *    total carbohydrate 0g; dietary fiber 0g; total sugars 0g; protein 23g; 
 *    vitamin D 0mcg; calcium 0mg; iron 0.9mg; potassium 0mg"
 * 
 * @param {string} labelText - The LABEL: text from camera AI
 * @param {Object} dailyValueReference - User's daily value targets
 * @returns {string} - Modified LABEL text with percentages converted to absolute amounts
 */
export function parseLabelAndConvertPercentages(labelText, dailyValueReference = null) {
  if (typeof labelText !== 'string' || !labelText.trim()) {
    return labelText;
  }

  const trimmed = labelText.trim();
  
  if (!trimmed.toUpperCase().startsWith('LABEL:')) {
    return labelText;
  }

  // Use provided daily values or return unchanged if not available
  if (!dailyValueReference || typeof dailyValueReference !== 'object') {
    console.warn('[AiLabelParser] No daily value reference provided, keeping percentages as-is');
    return labelText;
  }

  // Split by semicolons to get individual nutrient fields
  const parts = trimmed.split(';').map(part => part.trim());
  
  const convertedParts = parts.map(part => {
    // Look for patterns like "calcium 0%" or "iron 10%" or "iron 10%."
    // Allow optional trailing punctuation
    const percentMatch = part.match(/^([a-z\s]+?)\s+(\d+(?:\.\d+)?)\s*%\.?\s*$/i);
    
    if (!percentMatch) {
      return part; // Not a percentage-only field, keep as-is
    }

    const nutrientName = percentMatch[1].trim().toLowerCase();
    const percentValue = parseFloat(percentMatch[2]);

    // Look up the nutrient in our mappings
    const mapping = NUTRIENT_MAPPINGS[nutrientName];
    
    if (!mapping) {
      console.warn(`[AiLabelParser] Unknown nutrient "${nutrientName}", keeping as percentage`);
      return part;
    }

    // Get the daily value for this nutrient from user's targets
    const dailyValue = dailyValueReference[mapping.key];
    
    if (typeof dailyValue !== 'number' || dailyValue <= 0) {
      console.warn(`[AiLabelParser] No daily value for "${nutrientName}" (${mapping.key}), keeping as percentage`);
      return part;
    }

    // Calculate absolute amount: (percent / 100) * DV
    let absoluteAmount = (percentValue / 100) * dailyValue;

    // Round to 1 decimal place
    absoluteAmount = Math.round(absoluteAmount * 10) / 10;

    // Return converted format in lowercase to match original label format
    // Example: "iron 0.9mg" (using user's DV: 9mg × 10% = 0.9mg)
    const lowerCase = percentMatch[1].trim().toLowerCase();
      
    return `${lowerCase} ${absoluteAmount}${mapping.unit}`;
  });

  // Ensure we always include the 4 required micronutrients
  const requiredNutrients = ['vitamin d', 'calcium', 'iron', 'potassium'];
  const presentNutrients = new Set(
    convertedParts
      .map(part => {
        const match = part.match(/^([a-z\s]+?)\s+\d/i);
        return match ? match[1].trim().toLowerCase() : null;
      })
      .filter(Boolean)
  );

  // Add missing required nutrients with 0 values
  requiredNutrients.forEach(nutrientName => {
    if (!presentNutrients.has(nutrientName)) {
      const mapping = NUTRIENT_MAPPINGS[nutrientName];
      if (mapping) {
        const titleCase = nutrientName
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        convertedParts.push(`${titleCase} 0${mapping.unit}`);
      }
    }
  });

  return convertedParts.join('; ');
}

/**
 * Check if text is a LABEL: line
 */
export function isLabelText(text) {
  return typeof text === 'string' && text.trim().toUpperCase().startsWith('LABEL:');
}

/**
 * Check if text is a MEAL: line
 */
export function isMealText(text) {
  return typeof text === 'string' && text.trim().toUpperCase().startsWith('MEAL:');
}
